import { useEffect, useState } from "react";
import { Coins, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatPriceExact as formatPrice } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { PaymentReceiptsBody } from "./PaymentReceiptsModal";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";

const LIGHT_CLASS = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    yellow: "bg-amber-100 text-amber-800 border-amber-300",
    red: "bg-red-100 text-red-800 border-red-300",
};
const LIGHT_LABEL = { green: "Gut", yellow: "Knapp", red: "Kritisch" };

export function FinancialsChecklistModal({ listingId, condition, onBack, onClose, onSaved }) {
    const [fin, setFin] = useState(null);
    const [ek, setEk] = useState(0);
    const [vat, setVat] = useState(19);
    const [bonis, setBonis] = useState([]);
    const [costs, setCosts] = useState([]);
    const [notes, setNotes] = useState("");
    const [savingFin, setSavingFin] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get(`/listings/${listingId}/dispo/financials`).then((r) => r.data).catch(() => null)
            .then((f) => {
                if (!alive) return;
                if (f) {
                    setFin(f); setEk(f.purchase_price_net || 0); setVat(f.vat_rate || 19);
                    setBonis((f.bonis || []).map((b) => ({ ...b }))); setCosts((f.internal_costs || []).map((c2) => ({ ...c2 })));
                    setNotes(f.notes || "");
                }
            }).finally(() => { if (alive) setLoaded(true); });
        return () => { alive = false; };
    }, [listingId]);

    // Live-Marge (spiegelt Backend-Formel inkl. erhaltener Hersteller-Boni)
    const saleGross = Number(fin?.sale_gross || 0);
    const saleNet = saleGross ? Math.round((saleGross / (1 + (Number(vat) || 0) / 100)) * 100) / 100 : 0;
    const bonisTotal = bonis.reduce((s, b) => s + (Number(b.amount) || 0), 0);
    const costsTotal = costs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const mfaReceived = Number(fin?.manufacturer_bonis_received || 0);
    const mfaExpected = Number(fin?.manufacturer_bonis_expected || 0);
    const marginNet = Math.round((saleNet - (Number(ek) || 0) + bonisTotal + mfaReceived - costsTotal) * 100) / 100;
    const marginPct = saleNet > 0 ? Math.round((marginNet / saleNet) * 10000) / 100 : 0;
    const marginNetInclExpected = Math.round((marginNet + mfaExpected) * 100) / 100;
    const marginPctInclExpected = saleNet > 0 ? Math.round((marginNetInclExpected / saleNet) * 10000) / 100 : 0;
    const light = marginPct >= 12 ? "green" : marginPct >= 5 ? "yellow" : "red";

    const addLine = (setter) => setter((p) => [...p, { label: "", amount: 0 }]);
    const updLine = (setter, idx, k, v) => setter((p) => p.map((r, i) => i === idx ? { ...r, [k]: v } : r));
    const rmLine = (setter, idx) => setter((p) => p.filter((_, i) => i !== idx));

    const saveFin = async () => {
        setSavingFin(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/financials`, {
                purchase_price_net: Number(ek) || 0, vat_rate: Number(vat) || 19,
                bonis: bonis.map((b) => ({ label: b.label, amount: Number(b.amount) || 0 })),
                internal_costs: costs.map((c) => ({ label: c.label, amount: Number(c.amount) || 0 })),
                notes,
            });
            setFin(r.data.financials);
            toast.success(`Finanzen gespeichert · Marge ${formatPrice(r.data.financials.margin_net)} (${r.data.financials.margin_pct}%)`);
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSavingFin(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-financials">
                <FeatureGuideTriangle guideKey="financials" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Coins className="w-5 h-5 text-orange-600" /> Finanzen &amp; Zahlungen</DialogTitle></DialogHeader>

                {!loaded && <div className="flex items-center gap-2 text-sm text-gray-400 py-4" data-testid="dispo-financials-loading"><Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…</div>}

                {loaded && (
                    <div className="space-y-5">
                        {/* ===== Finanzen / Marge ===== */}
                        <div className="space-y-3" data-testid="dispo-financials-tab">
                            <div className={`rounded-sm border-2 p-3 flex items-center justify-between ${LIGHT_CLASS[light]}`} data-testid="dispo-margin-display">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">Marge (netto)</div>
                                    <div className="text-2xl font-black" data-testid="dispo-margin-amount">{formatPrice(marginNet)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-black" data-testid="dispo-margin-pct">{marginPct} %</div>
                                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/60" data-testid="dispo-margin-light">{LIGHT_LABEL[light]}</span>
                                </div>
                            </div>
                            <div className="text-[11px] text-gray-500 -mt-1">Verkaufserlös netto {formatPrice(saleNet)} (brutto {formatPrice(saleGross)} bei {vat}% USt) − EK + Boni + erhaltene Hersteller-Boni − interne Kosten</div>
                            {(mfaReceived > 0 || mfaExpected > 0) && (
                                <div className="rounded-sm border border-orange-200 bg-orange-50 p-2 text-[11px] text-orange-900 space-y-0.5" data-testid="dispo-manufacturer-bonis">
                                    {mfaReceived > 0 && <div className="flex justify-between"><span>Hersteller-Boni erhalten</span><span className="font-bold font-mono-num">+{formatPrice(mfaReceived)}</span></div>}
                                    {mfaExpected > 0 && <div className="flex justify-between"><span>Hersteller-Boni voraussichtlich (offen)</span><span className="font-bold font-mono-num">+{formatPrice(mfaExpected)}</span></div>}
                                    {mfaExpected > 0 && <div className="flex justify-between pt-0.5 border-t border-orange-200"><span>Marge inkl. voraussichtlicher Boni</span><span className="font-black font-mono-num">{formatPrice(marginNetInclExpected)} ({marginPctInclExpected} %)</span></div>}
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="text-xs">Einkaufspreis (EK) netto €</Label><Input type="number" min="0" step="0.01" value={ek} onChange={(e) => setEk(e.target.value)} className="font-mono-num" data-testid="dispo-fin-ek" /></div>
                                <div><Label className="text-xs">USt % (für Netto-Erlös)</Label><Input type="number" min="0" max="30" value={vat} onChange={(e) => setVat(e.target.value)} className="font-mono-num" data-testid="dispo-fin-vat" /></div>
                            </div>

                            <LineEditor title="Boni (Einnahmen +)" rows={bonis} setRows={setBonis} addLine={addLine} updLine={updLine} rmLine={rmLine} testid="dispo-fin-bonis" total={bonisTotal} />
                            <LineEditor title="Interne Kosten (Aufwand −)" rows={costs} setRows={setCosts} addLine={addLine} updLine={updLine} rmLine={rmLine} testid="dispo-fin-costs" total={costsTotal} />

                            <Textarea placeholder="Notizen (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm" data-testid="dispo-fin-notes" />
                            <Button type="button" onClick={saveFin} disabled={savingFin} className="bg-orange-500 hover:bg-orange-600 text-white w-full" data-testid="dispo-fin-save">
                                {savingFin ? <Loader2 className="w-4 h-4 animate-spin" /> : "Finanzen speichern"}
                            </Button>
                        </div>

                        {/* ===== Zahlungseingänge / Boni (vereint) ===== */}
                        <div className="border-t border-gray-200 pt-4" data-testid="dispo-receipts-tab">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Zahlungseingänge / Boni</h3>
                            <PaymentReceiptsBody listingId={listingId} condition={condition} embedded onSaved={onSaved} />
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <DispoBackButton onBack={onBack} />
                    <Button type="button" variant="outline" onClick={onClose}>Schließen</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function LineEditor({ title, rows, setRows, addLine, updLine, rmLine, testid, total }) {
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-bold uppercase tracking-wider">{title}</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addLine(setRows)} data-testid={`${testid}-add`}>+ Position</Button>
            </div>
            {rows.length === 0 && <div className="text-[11px] text-gray-400">Keine Position.</div>}
            {rows.map((r, idx) => (
                <div key={idx} className="flex items-center gap-2 py-0.5" data-testid={`${testid}-row`}>
                    <Input value={r.label} onChange={(e) => updLine(setRows, idx, "label", e.target.value)} placeholder="Bezeichnung" className="flex-1 h-8 text-sm" data-testid={`${testid}-label`} />
                    <Input type="number" step="0.01" value={r.amount} onChange={(e) => updLine(setRows, idx, "amount", e.target.value)} className="w-24 h-8 text-sm font-mono-num" data-testid={`${testid}-amount`} />
                    <button type="button" onClick={() => rmLine(setRows, idx)} className="text-red-500 hover:text-red-700"><XCircle className="w-4 h-4" /></button>
                </div>
            ))}
            {rows.length > 0 && <div className="flex justify-between text-[11px] font-bold border-t mt-1 pt-1"><span>Summe</span><span className="font-mono-num">{formatPrice(total)}</span></div>}
        </div>
    );
}
